import React from 'react'
import { usePermissions } from '../hooks/usePermissions'
import { useAuth } from '../hooks/useAuth'

interface PermissionGateProps {
  children: React.ReactNode
  permission?: 'editTasks' | 'editDeals' | 'editCustomers' | 'manageSms' | 'viewCustomers' | 'viewDeals' | 'viewTasks' | 'viewReports' | 'viewFinancials' | 'viewCallLogs' | 'exportData' | 'manageTeam' | 'manageSettings' | 'managePipeline' | 'manageProperties' | 'manageWorkflows' | 'manageIntegrations' | 'manageRoles' | 'viewAuditLogs' | 'viewTeamAnalytics'
  requireBroker?: boolean
  requireAdmin?: boolean
  fallback?: React.ReactNode
}

/**
 * Component that conditionally renders children based on user permissions
 */
export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  permission,
  requireBroker = false,
  requireAdmin = false,
  fallback = null
}) => {
  const { user, isAdmin, isBroker, isCollaborator } = useAuth()
  const { hasPermission, loading } = usePermissions()

  // Don't render anything while loading
  if (loading || !user) {
    return <>{fallback}</>
  }

  // Check admin requirement
  if (requireAdmin && !isAdmin) {
    return <>{fallback}</>
  }

  // Check broker requirement
  if (requireBroker && !isBroker) {
    return <>{fallback}</>
  }

  // Check specific permission requirement
  if (permission && !hasPermission(permission)) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

/**
 * Hook to check if user has specific permission
 */
export function usePermissionCheck() {
  const { isAdmin, isBroker } = useAuth()
  const { hasPermission } = usePermissions()

  return {
    canEditTasks: () => isAdmin || isBroker || hasPermission('editTasks'),
    canEditDeals: () => isAdmin || isBroker || hasPermission('editDeals'),
    canEditCustomers: () => isAdmin || isBroker || hasPermission('editCustomers'),
    canManageSms: () => isAdmin || isBroker || hasPermission('manageSms'),
    isViewOnly: () => !isAdmin && !isBroker && hasPermission('viewOnly')
  }
}